"use client";

import { EventTrackingProps } from "@/types/common";
import { Toast } from "@monsterindia/global-ui-components";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import ErrorBoundary from "@/components/common/errorBoundary";
import { fetchUserJobInfoAPI } from "@/components/dashboard/query";

import { ColumnType, savedPageLayoutConfig } from "../config";
import { useDashboardContext } from "../index";
import { savedPageMap } from "../map";
import "../saved/savedJobCard/components/styles.css";
import {
  SavedJobAPIDataType,
  SavedPageContextProps,
  SavedPageDataType,
} from "./types";

const SavedPageContext = createContext<SavedPageContextProps>({
  savedJobData: {} as SavedPageDataType,
  setSavedJobData: (data: SavedPageDataType) => {},
  handleEventTracking: (event: EventTrackingProps) => {},
  jobUserInfoHandler: () => {},
  cleverTapPropertyValues: {
    applied: "",
    recommended: "",
    saved: "",
  },
});

export function useSavedPageData() {
  return useContext(SavedPageContext);
}

const SavedPage = () => {
  const {
    isMobile,
    jobServerData,
    handleEventTracking,
    cleverTapPropertyValues,
  } = useDashboardContext();

  const [savedJobData, setSavedJobData] = useState<SavedPageDataType>({
    ...jobServerData.jobData,
    totalDataLength: jobServerData.jobData?.data?.length || 0,
    totalPagingLength: jobServerData.jobData?.meta?.paging?.total || 0,
    siteProps: jobServerData.siteProps,
    sitePropsProperties: jobServerData.sitePropsProperties,
    filterStatusPresent: false,
    savedJobs:
      jobServerData?.jobData?.data
        ?.filter((val: SavedJobAPIDataType) => val?.jobId)
        .map((val: SavedJobAPIDataType) => val.jobId) || [],
    profilePersonalDetails: jobServerData.profilePersonalDetails,
    isLoadingJobDesc: true,
    jobDescIndex: { selectedIndex: isMobile ? undefined : 0 },
    showMobileModal: false,
    autoApplyData: jobServerData.autoApplyData,
    similarJobsSelected: false,
    activeTab: jobServerData.activeTab,
    newDataLoader: jobServerData.clientTabSwitch,
    toastMessage: { text: "", type: "", show: false },
    userInfo: { appliedJobIds: [], savedJobIds: [] },
    dashboardHeaderRef: jobServerData.dashboardHeaderRef,
    windowData: {
      recaptchaV2Switch: jobServerData?.windowData?.recaptchaV2Switch || false,
      isSyndicateQuickApplyEnabled:
        jobServerData?.windowData?.isSyndicateQuickApplyEnabled || false,
      bulkApplyMultiSelect:
        jobServerData?.windowData?.bulkApplyMultiSelect || "",
    },
  });

  const [loader, setLoader] = useState(true);

  const jobUserInfoHandler = useCallback(
    async (jobs: SavedJobAPIDataType[]) => {
      const jobDataIds = jobs?.map((job: SavedJobAPIDataType) => job.id);
      const response = await fetchUserJobInfoAPI(jobDataIds);
      setSavedJobData((prevData: SavedPageDataType) => {
        const savedJobs = {
          ...prevData,
          userInfo: response,
        };
        const completeLinearData =
          savedJobs?.data?.map((job: SavedJobAPIDataType) => {
            return {
              ...job,
              isApplied:
                response?.appliedJobIds?.includes(job?.id || "") || false,
            };
          }) || [];

        savedJobs.data = [...completeLinearData];
        return savedJobs;
      });
    },
    [isMobile],
  );

  const layout = useMemo(() => {
    if (!isMobile) {
      return savedPageLayoutConfig.layouts.desktop;
    }
    return savedPageLayoutConfig.layouts.mobile;
  }, [isMobile]);

  const contextValue = useMemo(
    () => ({
      savedJobData,
      setSavedJobData,
      handleEventTracking,
      jobUserInfoHandler,
      cleverTapPropertyValues,
    }),
    [savedJobData, handleEventTracking, jobUserInfoHandler],
  );

  useEffect(() => {
    jobUserInfoHandler(savedJobData?.data || []);
  }, []);

  useEffect(() => {
    setSavedJobData((prevData: SavedPageDataType) => {
      const savedJobsModified = {
        ...prevData,
      };
      if (isMobile) savedJobsModified["jobDescIndex"] = { selectedIndex: 0 };
      return savedJobsModified;
    });
  }, [isMobile]);

  useEffect(() => {
    setTimeout(() => {
      setLoader(false);
    }, 0);
    handleEventTracking({
      eventName: "Pageviewed",
      eventType: "eventPush",
      eventProps: {
        page_name:
          cleverTapPropertyValues[
            savedJobData.activeTab as keyof typeof cleverTapPropertyValues
          ],
      },
    });
  }, []);

  useEffect(() => {
    setSavedJobData((prevData: SavedPageDataType) => {
      const savedJobsModified = {
        ...prevData,
        windowData: {
          recaptchaV2Switch:
            window?._siteProps_?.propertiesMap?.recaptchaV2Switch || false,
          isSyndicateQuickApplyEnabled:
            window?._siteProps_?.propertiesMap?.syndicatedQuickApplyEnabled ||
            false,
          bulkApplyMultiSelect:
            window?._siteProps_?.propertiesMap?.bulkApplyMultiSelect || "",
        },
      };
      return savedJobsModified;
    });
  }, []);

  const RenderComponent = ({ name }: { name: DashboardSectionKey }) => {
    const Component = savedPageMap[name];

    if (!Component) {
      return null;
    }

    return (
      <ErrorBoundary
        message={`Failed to load ${name.toLowerCase().replace(/_/g, " ")}`}
      >
        <Component />
      </ErrorBoundary>
    );
  };

  const renderSection = useCallback(
    (section: ColumnType[]) => {
      return section.map((row, rowIndex) => (
        <div key={rowIndex} className="flex w-full" id={row?.row[0]?.component}>
          {row.row.map((item, itemIndex) => (
            <div
              key={itemIndex}
              className="w-full"
              style={{
                ...item.styles,
                width: item.width,
                padding: item.padding,
              }}
            >
              <RenderComponent name={item.component} />
            </div>
          ))}
        </div>
      ));
    },
    [isMobile],
  );

  const renderRightcolumn = <div>{renderSection(layout.rightColumn)}</div>;

  return (
    <>
      <Toast
        timer={3000}
        showToast={savedJobData?.toastMessage?.show}
        text={savedJobData?.toastMessage?.text}
        type={savedJobData?.toastMessage?.type}
        onClose={() => {
          return setSavedJobData((prevData: SavedPageDataType) => {
            return {
              ...prevData,
              toastMessage: { text: "", type: "", show: false },
            };
          });
        }}
        width={isMobile ? "75" : "25"}
        position="top-center"
      />
      <SavedPageContext.Provider value={contextValue}>
        <div className={`${loader ? "hidden" : ""}`}>
          <div
            className="box-border flex h-full w-auto flex-col md:m-auto md:!flex-row md:items-start md:!gap-6"
            id="saved-dashboard-content"
          >
            {layout.leftColumn && (
              <div className="sticky z-[1001] md:!z-20 md:flex md:!w-[288px] md:shrink-0 md:grow-0">
                {renderSection(layout.leftColumn)}
              </div>
            )}
            {layout.middleColumn && (
              <div className={isMobile ? "w-full px-4" : "md:!w-[560px]"}>
                {renderSection(layout.middleColumn)}
              </div>
            )}
            {layout.rightColumn && (!isMobile ? renderRightcolumn : "")}
          </div>
        </div>
      </SavedPageContext.Provider>
    </>
  );
};

export default React.memo(SavedPage);
